import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { V2 } from "./tokens";
import { Eyebrow } from "./V2Shared";

/* Premium Vault. Every unlock maps to a REAL product surface (deep dives,
   live Entity Intelligence, market modules, regulation intelligence). CTA is
   entitlement-aware. Desktop (47:2 y5940) approved two-panel grammar kept at
   xl; Phase 3 touch (58:2 / 60:2): single violet-gradient card, outline
   ENTER PREMIUM pill, THE VAULT UNLOCKS dot list (2x2 at 768, stacked at 390). */
const UNLOCKS = [
  { label: "DEEP RESEARCH", desc: "Full-length deep dives with evidence and data notes", color: V2.violet },
  { label: "ENTITY INTELLIGENCE", desc: "Live protocol pages — TVL, topology, coverage", color: V2.cyan2 },
  { label: "MARKET CONTEXT", desc: "Real snapshot history behind every market claim", color: V2.green2 },
  { label: "POLICY TRACKING", desc: "Structured frameworks with confirmed status movement", color: V2.blue2 },
];

export const PremiumVault = ({ data }: { data: any }) => {
  const { user } = useAuth() as any;
  const isPremium = !!(user && (user.is_premium || user.subscription_tier === "premium" || user.role === "superadmin"));
  const dives = data?.premium_dives || [];
  const ctaLabel = isPremium ? "OPEN PREMIUM →" : "ENTER PREMIUM →";
  return (
    <section data-testid="v2-premium-vault" className="mt-[56px]">
      {/* ============ TOUCH (58:2 / 60:2) ============ */}
      <div className="xl:hidden relative rounded-[20px] md:rounded-[24px] overflow-hidden p-[18px] md:p-[24px]"
        style={{ background: "linear-gradient(150deg, rgba(8,7,13,0.96) 0%, rgba(27,13,62,0.96) 100%)", border: `1px solid ${V2.border2}` }}
        data-testid="v2-premium-vault-touch">
        <div aria-hidden className="absolute -top-[80px] -right-[60px] w-[360px] h-[360px] md:w-[500px] md:h-[500px] rounded-full"
          style={{ background: "radial-gradient(circle,#8b5cff36,transparent 62%)" }} />
        <div className="relative">
          <Eyebrow color={V2.violet} size={10}>PREMIUM INTELLIGENCE</Eyebrow>
          <h2 className="nl-sg text-[31px] leading-[36px] md:text-[36px] md:leading-[42px] font-medium mt-[12px]" style={{ color: V2.text }}>
            <span className="md:hidden">Go deeper than the headline.</span>
            <span className="hidden md:inline">Go beyond the front page.</span>
          </h2>
          <p className="nl-geist text-[13px] leading-[20px] md:text-[15px] md:leading-[23px] mt-[10px] max-w-[640px]" style={{ color: V2.body }}>
            {dives.length > 0
              ? `${dives.length} premium deep dive${dives.length === 1 ? "" : "s"} live now — plus entity intelligence, market context and policy tracking.`
              : "Research, entity intelligence and advanced context for readers who need second-order effects — not just the event."}
          </p>
          <Link to="/premium" data-testid="v2-premium-cta-touch"
            className={`${isPremium ? "" : "sparkle-btn "}nl-geist inline-flex items-center min-h-[44px] px-[20px] rounded-full text-[11px] font-semibold tracking-[0.08em] mt-[16px] transition-transform duration-200 hover:-translate-y-[2px]`}
            style={{ color: V2.violet, border: `1px solid ${V2.violet}`, background: "rgba(139,92,255,0.09)" }}>
            {ctaLabel}
          </Link>
          <p className="md:hidden nl-mono text-[9px] leading-[13px] tracking-[0.14em] mt-[18px]" style={{ color: V2.metaMuted }}>
            DEEP DIVES · SIGNALS · DATA
          </p>
          <p className="hidden md:block nl-geist text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.08em] uppercase mt-[24px]" style={{ color: V2.cyan2 }}>
            THE VAULT UNLOCKS
          </p>
          <div className="hidden md:grid md:grid-cols-2 gap-y-[20px] md:gap-y-[26px] gap-x-[24px] mt-[16px]">
            {UNLOCKS.map((u) => (
              <div key={u.label} className="flex gap-[12px]"
                data-testid={`v2-premium-unlock-touch-${u.label.toLowerCase().replace(/ /g, "-")}`}>
                <span aria-hidden className="w-[9px] h-[9px] md:w-[10px] md:h-[10px] rounded-full shrink-0 mt-[4px]" style={{ background: u.color }} />
                <div className="min-w-0">
                  <p className="nl-geist text-[13px] md:text-[14px] leading-[17px] md:leading-[18px] font-semibold" style={{ color: V2.text }}>{u.label}</p>
                  <p className="nl-geist text-[11px] md:text-[12px] leading-[16px] md:leading-[17px] mt-[3px]" style={{ color: V2.body }}>{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============ DESKTOP (master 200:645): title left · vault list right ============ */}
      <div className="relative rounded-[28px] overflow-hidden hidden xl:grid xl:grid-cols-[1fr_460px]"
        style={{ background: "linear-gradient(120deg,#150a33 0%,#0a0a14 55%,#03202b 100%)", border: `1px solid ${V2.border2}` }}>
        <div aria-hidden className="absolute -top-[120px] left-[30%] w-[520px] h-[520px] rounded-full"
          style={{ background: "radial-gradient(circle,#8b5cff24,transparent 62%)" }} />
        <div className="relative p-[34px]">
          <Eyebrow color={V2.violet} size={11}>PREMIUM INTELLIGENCE</Eyebrow>
          <h2 className="nl-sg text-[28px] leading-[34px] xl:text-[38px] xl:leading-[44px] font-medium mt-[14px]" style={{ color: V2.text }}>
            Go beyond the front page.
          </h2>
          <p className="nl-geist text-[16px] leading-[25px] mt-[12px] max-w-[560px]" style={{ color: V2.body }}>
            {dives.length > 0
              ? `${dives.length} premium deep dive${dives.length === 1 ? "" : "s"} live now, plus entity intelligence, market context and policy tracking.`
              : "Research, entity intelligence and advanced context for readers who need to understand second-order effects — not just the event."}
          </p>
          <Link to="/premium" data-testid="v2-premium-cta"
            className={`${isPremium ? "" : "sparkle-btn "}nl-geist inline-flex items-center h-[46px] px-[26px] rounded-full text-[12px] font-semibold tracking-[0.08em] mt-[24px] transition-transform duration-200 hover:-translate-y-[2px]`}
            style={{ color: V2.violet, border: `1px solid ${V2.violet}`, background: "rgba(139,92,255,0.09)" }}>
            {ctaLabel}
          </Link>
        </div>
        <div className="relative p-[34px] xl:border-l" style={{ borderColor: V2.border2 }}>
          <p className="nl-geist text-[11px] leading-[15px] font-semibold tracking-[0.08em] uppercase" style={{ color: V2.cyan2 }}>
            THE VAULT UNLOCKS
          </p>
          <div className="space-y-[18px] mt-[18px]">
            {UNLOCKS.map((u) => (
              <div key={u.label} data-testid={`v2-premium-unlock-${u.label.toLowerCase().replace(/ /g, "-")}`}>
                <div className="flex items-center gap-[12px]">
                  <span aria-hidden className="w-[9px] h-[9px] rounded-full shrink-0" style={{ background: u.color }} />
                  <p className="nl-geist text-[14px] leading-[18px] font-semibold" style={{ color: V2.text }}>{u.label}</p>
                </div>
                <p className="nl-geist text-[12px] leading-[18px] mt-[4px] pl-[21px]" style={{ color: V2.body }}>{u.desc}</p>
                <div aria-hidden className="h-px mt-[14px]" style={{ background: V2.border2 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
